import { useEffect, useState } from "react";

import type { CommercialContext, CommercialContextRequest } from "../../types/aiReputation";
import { extractApiErrorMessage } from "../../utils/aiReputation";

interface Props {
  context: CommercialContext | null;
  onSave: (context: CommercialContextRequest) => Promise<void>;
  onReload: () => void;
}
const blank = (): CommercialContext => ({
  intended_positioning: "",
  priority_guest_personas: [],
  reasons_to_choose: [],
  target_needs_occasions: [],
  real_competitors: [],
  revision: 0,
  updated_at: null,
  is_complete: false,
});
const fields = [
  [
    "priority_guest_personas",
    "Priority target personas",
    "Who do you most want to attract?",
    "e.g. Solo business traveller on a mid-week stay",
  ],
  [
    "reasons_to_choose",
    "Reasons to choose",
    "What makes guests pick you over a competitor?",
    "e.g. Rooftop restaurant with panoramic city views",
  ],
  [
    "target_needs_occasions",
    "Target needs and occasions",
    "What jobs-to-be-done does your hotel serve?",
    "e.g. Corporate retreat, romantic weekend break",
  ],
] as const;

export const CommercialContextEditor = ({ context, onSave, onReload }: Props) => {
  const [draft, setDraft] = useState(context ?? blank());
  const [values, setValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setDraft(context ?? blank()), [context]);
  const add = (field: (typeof fields)[number][0]) => {
    const value = values[field]?.trim();
    if (
      !value ||
      draft[field].some((item) => item.toLocaleLowerCase() === value.toLocaleLowerCase())
    )
      return;
    setDraft({ ...draft, [field]: [...draft[field], value] });
    setValues({ ...values, [field]: "" });
  };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setConflict(false);
    try {
      await onSave({
        intended_positioning: draft.intended_positioning,
        priority_guest_personas: draft.priority_guest_personas,
        reasons_to_choose: draft.reasons_to_choose,
        target_needs_occasions: draft.target_needs_occasions,
        real_competitors: draft.real_competitors,
        expected_revision: draft.revision,
      });
    } catch (caught: unknown) {
      const message = extractApiErrorMessage(caught, "Unable to save commercial context.");
      if (message.includes("context_revision_conflict")) setConflict(true);
      else setError(message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-5 rounded-2xl bg-surface-container-low p-6">
      <div>
        <h2 className="font-headline text-xl font-bold">Commercial context</h2>
        <p className="text-sm text-on-surface-variant">
          This strategy is used only for future reports.
        </p>
      </div>
      <label className="block text-sm font-medium">
        Intended positioning
        <p className="mt-0.5 text-xs font-normal text-on-surface-variant">
          How do you want AI to describe your hotel? Write it as a positioning statement.
        </p>
        <textarea
          required
          minLength={10}
          maxLength={1000}
          placeholder="e.g. A boutique city-centre hotel for business travellers seeking a quiet, design-led stay close to the financial district."
          value={draft.intended_positioning}
          onChange={(event) => setDraft({ ...draft, intended_positioning: event.target.value })}
          className="mt-1 min-h-24 w-full rounded-lg border border-outline-variant bg-surface-container-lowest p-3"
        />
      </label>
      {fields.map(([field, label, hint, placeholder]) => (
        <div key={field}>
          <label htmlFor={field} className="block text-sm font-medium">
            {label}
          </label>
          <p className="mt-0.5 text-xs text-on-surface-variant">{hint}</p>
          <div className="mt-1 flex gap-2">
            <input
              id={field}
              placeholder={placeholder}
              value={values[field] ?? ""}
              onChange={(event) => setValues({ ...values, [field]: event.target.value })}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  add(field);
                }
              }}
              className="min-w-0 flex-1 rounded-lg border border-outline-variant bg-surface-container-lowest p-3"
            />
            <button
              type="button"
              onClick={() => add(field)}
              className="rounded-lg bg-surface-container-high px-4"
            >
              Add
            </button>
          </div>
          <ul className="mt-2 flex flex-wrap gap-2">
            {draft[field].map((value) => (
              <li key={value} className="rounded-full bg-primary/10 px-3 py-1 text-sm text-primary">
                {value}
                <button
                  type="button"
                  aria-label={`Remove ${value}`}
                  onClick={() =>
                    setDraft({ ...draft, [field]: draft[field].filter((item) => item !== value) })
                  }
                  className="ml-2"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <fieldset>
        <legend className="text-sm font-medium">Real competitors</legend>
        <p className="mt-0.5 text-xs text-on-surface-variant">
          Hotels you directly compete with. Used to flag when AI favours a competitor source over
          yours.
        </p>
        <div className="mt-1 hidden sm:grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <span className="text-xs text-on-surface-variant">Competitor name</span>
          <span className="text-xs text-on-surface-variant">Website URL (optional)</span>
          <span />
        </div>
        {draft.real_competitors.map((competitor, index) => (
          <div
            key={`${competitor.name}-${index}`}
            className="mt-2 grid gap-2 sm:grid-cols-[1fr_1fr_auto]"
          >
            <input
              aria-label={`Competitor ${index + 1} name`}
              placeholder="e.g. The Grand Hotel"
              value={competitor.name}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  real_competitors: draft.real_competitors.map((item, itemIndex) =>
                    itemIndex === index ? { ...item, name: event.target.value } : item,
                  ),
                })
              }
              className="rounded-lg border border-outline-variant bg-surface-container-lowest p-3"
            />
            <input
              aria-label={`Competitor ${index + 1} website`}
              type="url"
              placeholder="e.g. https://thegrandhotel.com (optional)"
              value={competitor.website_url ?? ""}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  real_competitors: draft.real_competitors.map((item, itemIndex) =>
                    itemIndex === index
                      ? { ...item, website_url: event.target.value || null }
                      : item,
                  ),
                })
              }
              className="rounded-lg border border-outline-variant bg-surface-container-lowest p-3"
            />
            <button
              type="button"
              onClick={() =>
                setDraft({
                  ...draft,
                  real_competitors: draft.real_competitors.filter(
                    (_, itemIndex) => itemIndex !== index,
                  ),
                })
              }
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            setDraft({
              ...draft,
              real_competitors: [...draft.real_competitors, { name: "", website_url: null }],
            })
          }
          className="mt-2 text-primary"
        >
          Add competitor
        </button>
      </fieldset>
      {conflict && (
        <div role="alert" className="rounded-lg border border-error p-3">
          This context changed elsewhere.{" "}
          <button type="button" className="font-bold underline" onClick={onReload}>
            Reload the latest context
          </button>{" "}
          before saving.
        </div>
      )}
      {error && (
        <p role="alert" className="text-error">
          {error}
        </p>
      )}
      <button
        disabled={saving}
        className="rounded-xl bg-primary px-5 py-3 font-bold text-on-primary"
      >
        {saving ? "Saving..." : "Save commercial context"}
      </button>
    </form>
  );
};
